import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';

export default function BlogPost(){
  const { slug } = useParams();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [exiting, setExiting] = useState(false);
  const navigate = useNavigate();
  const navTimer = useRef(null);

  useEffect(()=>{
    let mounted = true;
    (async ()=>{
      try{
        const res = await fetch(`/posts/${slug}.md`);
        if(!res.ok) throw new Error('Not found');
        const txt = await res.text();
        if(mounted) {
          // parse title and optional date from top of markdown
          const lines = txt.split(/\r?\n/);
          let i = 0;
          let parsedTitle = '';
          let parsedDate = '';

          // find first non-empty line
          while(i < lines.length && lines[i].trim() === '') i++;
          if(i < lines.length && /^#\s+/.test(lines[i])){
            parsedTitle = lines[i].replace(/^#\s+/, '').trim();
            i++;
            // skip possible empty line
            if(i < lines.length && lines[i].trim() === '') i++;
            // date line like *YYYY-MM-DD* or * YYYY-MM-DD *
            if(i < lines.length && /^\*\s*\d{4}-\d{2}-\d{2}\s*\*/.test(lines[i])){
              parsedDate = lines[i].replace(/\*/g,'').trim();
              i++;
              if(i < lines.length && lines[i].trim() === '') i++;
            }
          }

          const body = lines.slice(i).join('\n');
          setTitle(parsedTitle);
          setDate(parsedDate);
          setContent(body);
        }
      }catch(e){
        if(mounted) setContent('# Post not found');
      }finally{ if(mounted) setLoading(false); }
    })();
    return ()=>{ mounted = false };
  },[slug]);

  // keep a ref to the article and ensure we start at top when opening a post
  const articleRef = useRef(null);
  useEffect(()=>{
    window.scrollTo({ top: 0, behavior: 'auto' });
    // small focus for accessibility
    const t = setTimeout(()=>{ if(articleRef.current) articleRef.current.focus?.(); }, 80);
    return ()=> clearTimeout(t);
  },[slug]);

  const handleBack = (e) => {
    if (e) e.preventDefault();
    if (loading) return navigate('/blog');
    if (exiting) return;
    setExiting(true);
    // wait for exit animation then navigate
    navTimer.current = setTimeout(() => navigate('/blog'), 280);
  };

  // cleanup any pending navigation timer when the component unmounts
  useEffect(()=>{
    return ()=>{ if(navTimer.current) clearTimeout(navTimer.current); };
  },[]);

  return (
    <section className="blog-post-section" style={{ paddingTop: '20px', paddingBottom: '80px', background: 'transparent', minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ marginBottom: '18px' }}>
          <a href="/blog" onClick={handleBack} className="back-btn">← Back to posts</a>
        </div>
        <div>
          {loading ? (
            <div style={{ minHeight: 'calc(60vh)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>
          ) : (
            <article ref={articleRef} tabIndex={-1} className={`blog-content glass-card ${exiting ? 'post-exit' : 'post-open'}`} style={{ padding: 'clamp(14px, 4vw, 36px)' }} aria-live="polite">
              {title && <h1 className="blog-title" style={{ marginTop: 0 }}>{title}</h1>}
              {date && <div className="blog-meta" style={{ color: 'var(--muted)', marginBottom: '18px' }}>{new Date(date).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })} · Dabra team</div>}
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeRaw, rehypeKatex]}>
                {content}
              </ReactMarkdown>
            </article>
          )}
        </div>
      </div>
    </section>
  );
}

